"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const milestones = [{
  year: "2024",
  title: "The birth of an idea",
  description: "A new parent realizes the need for a simple, portable way to maintain hygiene before interacting with their newborn."
}, {
  year: "2024",
  title: "Formulating the mist",
  description: "We develop a gentle, effective formula that fits perfectly into the busy, on-the-go lifestyle of modern families."
}, {
  year: "2025",
  title: "Launching Before You Enter™",
  description: "Our portable hygiene mist officially hits the market, helping parents and travelers stay prepared for every moment."
}, {
  year: "2025",
  title: "Building a community",
  description: "Thousands of families join our mission, sharing how B.Y.E. has brought peace of mind to their daily routines."
}, {
  year: "2026",
  title: "Expanding our reach",
  description: "From childcare centers to international travel, we continue to make personal hygiene simple, approachable, and portable."
}];
function ScribbleUnderline() {
  return <svg aria-hidden="true" viewBox="0 0 220 18" preserveAspectRatio="none" className="absolute left-0 right-0 -bottom-2 w-full h-3 overflow-visible">
      <path d="M3 11 C 35 4, 70 16, 105 9 S 175 4, 217 11" fill="none" stroke="var(--primary)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" pathLength={1} style={{
      strokeDasharray: 1,
      strokeDashoffset: 0,
      animation: "tinker-scribble-draw 600ms ease-out both"
    }} />
    </svg>;
}
export default function Timeline() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="6189" id="the-bye-timeline" className="bg-background text-foreground relative border-y border-border py-24 md:py-32 px-6 md:px-10 lg:px-16 overflow-hidden">
      <style>{`
        @keyframes tinker-scribble-draw {
          from { stroke-dashoffset: 1; }
          to { stroke-dashoffset: 0; }
        }
        @keyframes tinker-rise {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div className="mx-auto max-w-[820px]">
        {}
        <div ref={headerRef} className="text-center" style={headerVisible ? {
        animation: "tinker-rise 520ms cubic-bezier(0.22, 1, 0.36, 1) both"
      } : {
        opacity: 0
      }}>
          <h2 className="font-serif font-medium text-foreground tracking-tight leading-[1.04]" style={{
          fontSize: "clamp(2rem, 4.2vw, 3.75rem)"
        }}>
            The B.Y.E.{" "}
            <span className="relative inline-block italic font-normal">
              journey
              <ScribbleUnderline />
            </span>
          </h2>
          <p className="mt-6 font-default text-foreground/80 text-base md:text-lg leading-relaxed">
            From a simple parental insight to a trusted companion for your family.
          </p>
        </div>

        {}
        <div className="relative mt-16 md:mt-20">
          {}
          <div className="md:hidden absolute top-0 bottom-0 w-1 rounded-md" style={{
          left: "1rem",
          backgroundColor: "var(--border)"
        }} aria-hidden="true" />
          {}
          <div className="hidden md:block absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-1 rounded-md" style={{
          backgroundColor: "var(--border)"
        }} aria-hidden="true" />

          <ul className="relative flex flex-col gap-10 md:gap-14">
            {milestones.map((m, i) => {
            const isLeft = i % 2 === 0;
            return <TimelineEntry key={m.year} data-index={i} index={i} isLeft={isLeft} year={m.year} title={m.title} description={m.description} />;
          })}
          </ul>
        </div>
      </div>
    </section>;
}
function TimelineEntry({
  index,
  isLeft,
  year,
  title,
  description,
  ...rest
}: {
  index: number;
  isLeft: boolean;
  year: string;
  title: string;
  description: string;
  "data-index"?: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLLIElement>({
    threshold: 0.2
  });
  return <li ref={ref} {...rest} className="relative md:grid md:grid-cols-2 md:gap-10" style={isVisible ? {
    animation: `tinker-rise 520ms cubic-bezier(0.22, 1, 0.36, 1) both`,
    animationDelay: `${index * 100}ms`
  } : {
    opacity: 0
  }}>
      {}
      <span className="md:hidden absolute top-6 size-3 rounded-lg z-10" style={{
      left: "1rem",
      transform: "translateX(-50%)",
      backgroundColor: "var(--primary)"
    }} aria-hidden="true" />
      {}
      <span className="hidden md:block absolute top-8 left-1/2 -translate-x-1/2 size-3 rounded-lg z-10" style={{
      backgroundColor: "var(--primary)"
    }} aria-hidden="true" />

      {}
      {isLeft ? <>
          <div className="hidden md:flex md:justify-end md:pr-10">
            <TimelineCard year={year} title={title} description={description} />
          </div>
          <div className="md:hidden pl-10">
            <TimelineCard year={year} title={title} description={description} />
          </div>
          <div className="hidden md:block" aria-hidden="true" />
        </> : <>
          <div className="hidden md:block" aria-hidden="true" />
          <div className="hidden md:flex md:justify-start md:pl-10">
            <TimelineCard year={year} title={title} description={description} />
          </div>
          <div className="md:hidden pl-10">
            <TimelineCard year={year} title={title} description={description} />
          </div>
        </>}
    </li>;
}
function TimelineCard({
  year,
  title,
  description
}: {
  year: string;
  title: string;
  description: string;
}) {
  return <div className="w-full md:max-w-[360px] rounded-3xl border border-border p-6 shadow-md" style={{
    backgroundColor: "var(--card)"
  }}>
      <div className="font-serif font-medium tracking-tight text-card-foreground" style={{
      fontSize: "clamp(1.5rem, 2.4vw, 2.125rem)",
      lineHeight: 1.15
    }}>
        {year}
      </div>
      <h3 className="mt-2 font-default font-semibold text-card-foreground text-lg md:text-xl tracking-tight leading-snug">
        {title}
      </h3>
      <p className="mt-3 font-default text-card-foreground/80 text-base leading-relaxed">
        {description}
      </p>
    </div>;
}